/**
 * Email utility functions for consistent email handling
 */

/**
 * Formats a phone number for display
 * @param {string} phone - The phone number to format
 * @returns {string} - The formatted phone number
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return "No phone";

  // Remove all non-numeric characters
  const phoneNumber = phone.replace(/\D/g, "");

  // Don't format if empty or too short
  if (phoneNumber.length === 0) return "No phone";
  if (phoneNumber.length < 4) return phoneNumber;

  // Format based on length
  if (phoneNumber.length < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  } else if (phoneNumber.length < 11) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
      3,
      6
    )}-${phoneNumber.slice(6)}`;
  } else {
    // Handle longer numbers (with country code)
    return `+${phoneNumber.slice(
      0,
      phoneNumber.length - 10
    )} (${phoneNumber.slice(-10, -7)}) ${phoneNumber.slice(
      -7,
      -4
    )}-${phoneNumber.slice(-4)}`;
  }
};

/**
 * Formats an email address to lowercase
 * @param {string} email - The email address to format
 * @returns {string} - The email address in lowercase
 */
export const formatEmailToLowercase = (email) => {
  if (!email || typeof email !== "string") return "";
  return email.toLowerCase().trim();
};

/**
 * Handles email input change events to ensure lowercase formatting
 * @param {Event} event - The input change event
 * @param {Function} setValue - Function to set the input value
 * @param {string} fieldName - The name of the field being updated
 */
export const handleEmailInputChange = (event, setValue, fieldName) => {
  const value = formatEmailToLowercase(event.target.value);
  setValue(fieldName, value);
};

/**
 * Validates and formats email for form submission
 * @param {string} email - The email to validate and format
 * @returns {string} - The formatted email or empty string if invalid
 */
export const validateAndFormatEmail = (email) => {
  if (!email) return "";

  const formattedEmail = formatEmailToLowercase(email);

  // Basic email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(formattedEmail)) {
    return "";
  }

  return formattedEmail;
};
